import {
  can,
  INTERNAL_SOURCES,
  QUESTION_ACTIONS,
  QUESTION_KINDS,
  QUESTION_STATUSES,
  type QuestionAction,
  type QuestionDetail,
  type QuestionSummary,
  REMARK_ACTIONS,
  REMARK_KINDS,
  REMARK_SOURCES,
  REMARK_STATUSES,
  type RemarkAction,
  type RemarkDetail,
  type RemarkKind,
  type RemarkSource,
  type RemarkSummary,
  ROOT_CAUSES,
  type RootCause,
  type SetSummary,
  type SupervisionStats,
  URGENCY,
  type Urgency,
  type VisitDetail,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { AlertTriangle, MapPin, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { DiscussionOriginBanner } from '../../components/DiscussionOrigin';
import { DiscussionPanel } from '../../components/DiscussionPanel';
import { FieldBasis } from '../../components/FieldBasis';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, todayLocal } from '../../lib/format';
import { counterpartiesQuery, projectQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel, TitleBlock } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { NewChangeDialog } from '../changes/ChangesPage';
import {
  FileLink,
  PHOTO_ACCEPT,
  ProjectFilter,
  projectTarget,
  QuestionBadge,
  RemarkBadge,
  Tabs,
  UploadButton,
  useProjectDefault,
  useProjectsWith,
} from '../psd/shared';
import { sitePhotosLink } from '../site/SitePhotosPage';
import { type ExtraFilter, ExtraFilters, extraQuery } from './Filters';
import { InspectionsList, NewInspectionDialog } from './InspectionPage';
import { QuestionWorks, ReminderBadge } from './QuestionWorks';
import { ReportPanel } from './Report';
import { SettingsPanel, useSupervisionSettings } from './Settings';
import { NewVisitDialog, VisitsList } from './VisitPage';

type Tab = 'remarks' | 'questions' | 'stats' | 'visits' | 'inspections' | 'report';
type Search = { project?: string; tab?: Tab; set?: string; status?: string; source?: string } & ExtraFilter;

const plusDays = (n: number) => todayLocal(new Date(Date.now() + n * 86_400_000));

/* ───────────────────────── Реестр ───────────────────────── */

export function SupervisionPage() {
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  const tab: Tab = search.tab ?? 'remarks';
  const setSearch = (patch: Partial<Search>) =>
    navigate({ to: '/supervision', search: { ...search, ...patch } as never, replace: true });
  // Для отложенного ввода текста: новые значения накладываются на текущий адрес
  const setSearchStable = useCallback(
    (patch: Partial<Search>) =>
      navigate({
        to: '/supervision',
        search: ((prev: Search) => ({ ...prev, ...patch })) as never,
        replace: true,
      }),
    [navigate],
  );
  const issuers = useProjectsWith('supervision.issue');
  const askers = useProjectsWith('supervision.issue', 'supervision.respond');
  const [newRemark, setNewRemark] = useState(false);
  const [newQuestion, setNewQuestion] = useState(false);
  const [newVisit, setNewVisit] = useState(false);
  const [newInspection, setNewInspection] = useState(false);
  const callers = useProjectsWith('supervision.respond');
  // Отчёт готовит сотрудник с правом «Видеть реестр надзора»
  const reporters = useProjectsWith('supervision.view');
  const staff = (useMe().tenant?.staff ?? false) && reporters.list.length > 0;
  useCrumbs(
    tab === 'remarks'
      ? null
      : [
          {
            label:
              tab === 'questions'
                ? 'Вопросы к проекту'
                : tab === 'visits'
                  ? 'Журнал посещений'
                  : tab === 'inspections'
                    ? 'Освидетельствование'
                    : tab === 'report'
                      ? 'Отчёт и протокол'
                      : 'Сроки и просрочки',
          },
        ],
  );
  return (
    <Page>
      {/* На печати отчёта — только сам отчёт */}
      <div className={tab === 'report' ? 'print:hidden' : undefined}>
        <PageHeader
          title="Надзор и вопросы к проекту"
          subtitle="Единый реестр: предписания технадзора, авторский надзор, экспертиза, ОТК, ОТ и ПБ, вопросы строителей проектировщику."
          actions={
            tab === 'questions' && askers.list.length ? (
              <Button
                variant="primary"
                size="lg"
                icon={<Plus className="size-4" />}
                onClick={() => setNewQuestion(true)}
              >
                Новый вопрос к проекту
              </Button>
            ) : tab === 'inspections' && callers.list.length ? (
              <Button
                variant="primary"
                size="lg"
                icon={<Plus className="size-4" />}
                onClick={() => setNewInspection(true)}
              >
                Новый вызов
              </Button>
            ) : tab === 'visits' && issuers.list.length ? (
              <Button
                variant="primary"
                size="lg"
                icon={<Plus className="size-4" />}
                onClick={() => setNewVisit(true)}
              >
                Новое посещение
              </Button>
            ) : tab === 'remarks' && issuers.list.length ? (
              <Button
                variant="primary"
                size="lg"
                icon={<Plus className="size-4" />}
                onClick={() => setNewRemark(true)}
              >
                Новое замечание
              </Button>
            ) : null
          }
        />
        <Tabs
          value={tab}
          onChange={(v) => setSearch({ tab: v === 'remarks' ? undefined : v, status: undefined })}
          items={[
            { value: 'remarks', label: 'Замечания и предписания' },
            { value: 'questions', label: 'Вопросы к проекту' },
            { value: 'visits', label: 'Журнал посещений' },
            { value: 'inspections', label: 'Освидетельствование' },
            { value: 'stats', label: 'Сроки и просрочки' },
            ...(staff ? [{ value: 'report' as const, label: 'Отчёт и протокол' }] : []),
          ]}
        />
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:flex-wrap">
          <ProjectFilter
            value={search.project}
            onChange={(v) => setSearch({ project: v, set: undefined })}
            perms={['supervision.view', 'supervision.respond']}
          />
          {tab !== 'stats' && tab !== 'visits' && tab !== 'inspections' && tab !== 'report' ? (
            <Select
              aria-label="Состояние"
              className="h-11 md:h-10 md:w-48"
              value={search.status ?? 'open'}
              onChange={(e) => setSearch({ status: e.target.value === 'open' ? undefined : e.target.value })}
            >
              <option value="open">Открытые</option>
              <option value="overdue">Просроченные</option>
              <option value="closed">Закрытые</option>
              <option value="all">Все</option>
            </Select>
          ) : null}
          {tab === 'remarks' ? (
            <Select
              aria-label="Источник"
              className="h-11 md:h-10 md:w-56"
              value={search.source ?? ''}
              onChange={(e) => setSearch({ source: e.target.value || undefined })}
            >
              <option value="">Все источники</option>
              {(Object.keys(REMARK_SOURCES) as RemarkSource[]).map((k) => (
                <option key={k} value={k}>
                  {REMARK_SOURCES[k]}
                </option>
              ))}
            </Select>
          ) : null}
          {tab === 'remarks' || tab === 'questions' ? (
            <ExtraFilters
              value={search}
              projectId={search.project}
              remarks={tab === 'remarks'}
              onChange={setSearchStable}
            />
          ) : null}
          {search.set ? (
            <Button variant="ghost" className="h-11 md:h-10" onClick={() => setSearch({ set: undefined })}>
              Сбросить отбор по комплекту
            </Button>
          ) : null}
        </div>
      </div>
      {tab === 'remarks' ? <RemarksList search={search} /> : null}
      {tab === 'questions' ? <QuestionsList search={search} /> : null}
      {tab === 'stats' ? <StatsPanel projectId={search.project} /> : null}
      {tab === 'stats' ? <SettingsPanel /> : null}
      {tab === 'visits' ? <VisitsList projectId={search.project} /> : null}
      {tab === 'inspections' ? <InspectionsList projectId={search.project} /> : null}
      {tab === 'report' ? <ReportPanel projectId={search.project} /> : null}
      {newInspection ? (
        <NewInspectionDialog projectId={search.project} onClose={() => setNewInspection(false)} />
      ) : null}
      {newVisit ? <NewVisitDialog projectId={search.project} onClose={() => setNewVisit(false)} /> : null}
      {newRemark ? <NewRemarkDialog projectId={search.project} onClose={() => setNewRemark(false)} /> : null}
      {newQuestion ? (
        <NewQuestionDialog projectId={search.project} onClose={() => setNewQuestion(false)} />
      ) : null}
    </Page>
  );
}

export function RemarksList({ search, compact }: { search: Search & { sheet?: string }; compact?: boolean }) {
  const q = useQuery({
    queryKey: [
      'remarks',
      search.project,
      search.set,
      search.sheet,
      search.status,
      search.source,
      compact ? null : extraQuery(search, true),
    ],
    queryFn: () =>
      api<RemarkSummary[]>('/supervision/remarks', {
        query: {
          projectId: search.project,
          setId: search.set,
          sheetId: search.sheet,
          status: search.status ?? (compact ? 'all' : 'open'),
          source: search.source,
          ...(compact ? {} : extraQuery(search, true)),
        },
      }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data.length)
    return compact ? (
      <p className="text-[14px] text-ink-2">Замечаний нет.</p>
    ) : (
      <EmptyState title="Замечаний нет">
        Предписания и замечания с фото, сроком и местом на листе появятся здесь.
      </EmptyState>
    );
  return (
    <ul className="flex flex-col gap-2">
      {q.data.map((r) => (
        <li key={r.id}>
          <Link
            to="/supervision/remarks/$remarkId"
            params={{ remarkId: r.id }}
            className={clsx(
              'flex flex-col gap-1.5 rounded-lg border border-line bg-surface p-4 hover:border-ink-3',
              !compact && 'md:flex-row md:items-start md:gap-4',
            )}
          >
            <span className="num shrink-0 font-medium text-ink md:w-24">{r.number}</span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-ink [overflow-wrap:anywhere]">{r.title}</span>
              <span className="text-[13px] text-ink-2">
                {REMARK_SOURCES[r.source]} · {REMARK_KINDS[r.kind]}
                {r.setCode ? ` · ${r.setCode}, лист ${r.sheetNumber}` : ''}
                {r.contractorName ? ` · ${r.contractorName}` : ''}
                {r.assigneeName ? ` · отв. ${r.assigneeName}` : ''}
              </span>
            </span>
            <span className="flex flex-wrap items-center gap-1.5 md:justify-end">
              {r.stopWork ? <Badge tone="danger">остановка работ</Badge> : null}
              {r.internal ? <Badge>внутреннее</Badge> : null}
              <RemarkBadge status={r.status} overdue={r.overdue} />
              <span className="text-[13px] text-ink-2">до {date(r.dueDate)}</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function QuestionsList({
  search,
  compact,
}: {
  search: Search & { sheet?: string };
  compact?: boolean;
}) {
  const q = useQuery({
    queryKey: [
      'questions',
      search.project,
      search.set,
      search.sheet,
      search.status,
      compact ? null : extraQuery(search, false),
    ],
    queryFn: () =>
      api<QuestionSummary[]>('/supervision/questions', {
        query: {
          projectId: search.project,
          setId: search.set,
          sheetId: search.sheet,
          status: search.status ?? (compact ? 'all' : 'open'),
          ...(compact ? {} : extraQuery(search, false)),
        },
      }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data.length)
    return compact ? (
      <p className="text-[14px] text-ink-2">Вопросов нет.</p>
    ) : (
      <EmptyState title="Вопросов нет">
        Вопрос к проекту — с листом, сутью, фото, предлагаемым решением и тем, какие работы стоят.
      </EmptyState>
    );
  return (
    <ul className="flex flex-col gap-2">
      {q.data.map((x) => (
        <li key={x.id}>
          <Link
            to="/supervision/questions/$questionId"
            params={{ questionId: x.id }}
            className={clsx(
              'flex flex-col gap-1.5 rounded-lg border border-line bg-surface p-4 hover:border-ink-3',
              !compact && 'md:flex-row md:items-start md:gap-4',
            )}
          >
            <span className="num shrink-0 font-medium text-ink md:w-24">{x.number}</span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-ink [overflow-wrap:anywhere]">{x.subject}</span>
              <span className="text-[13px] text-ink-2">
                {x.kind === 'proposal' ? 'Предложение · ' : ''}
                {x.setCode ? `${x.setCode}, лист ${x.sheetNumber} · ` : ''}
                {x.authorName ?? ''}
                {x.side ? ` · сейчас: ${x.side}` : ''}
              </span>
            </span>
            <span className="flex flex-wrap items-center gap-1.5 md:justify-end">
              {x.urgency !== 'normal' ? (
                <Badge tone={x.urgency === 'critical' ? 'danger' : 'warning'}>{URGENCY[x.urgency]}</Badge>
              ) : null}
              {x.changesDesign ? <Badge tone="accent">меняет проект</Badge> : null}
              <QuestionBadge status={x.status} overdue={x.overdue} />
              {x.dueDate && (x.status === 'designer' || x.status === 'customer') ? (
                <span className="text-[13px] text-ink-2">ответ до {date(x.dueDate)}</span>
              ) : null}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function StatsPanel({ projectId }: { projectId?: string }) {
  const q = useQuery({
    queryKey: ['supervision-stats', projectId ?? 'all'],
    queryFn: () => api<SupervisionStats>('/supervision/stats', { query: { projectId } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const s = q.data;
  return (
    <div className="flex flex-col gap-4">
      <TitleBlock
        items={[
          { label: 'Открыто замечаний', value: s.remarks.open },
          { label: 'Просрочено', value: s.remarks.overdue, tone: s.remarks.overdue ? 'danger' : 'default' },
          {
            label: 'С остановкой работ',
            value: s.remarks.stopWork,
            tone: s.remarks.stopWork ? 'danger' : 'default',
          },
          {
            label: 'Вопросов к проекту',
            value: s.questions.open,
            note: s.questions.overdue ? `просрочено ответов: ${s.questions.overdue}` : 'без просрочек',
          },
          {
            label: 'Средний ответ проектировщика',
            value: s.avgAnswerDays === null ? '—' : `${s.avgAnswerDays} р. дн.`,
          },
        ]}
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="По источникам" bodyClassName="p-0">
          <ScrollX>
            <table className="w-full min-w-[480px] text-[14px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                  <th className="py-2.5 pr-3 pl-5 font-medium">Источник</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Открыто</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Просрочено</th>
                  <th className="py-2.5 pr-5 text-right font-medium">Устранение, дней</th>
                </tr>
              </thead>
              <tbody>
                {s.bySource.map((r) => (
                  <tr key={r.source} className="border-t border-line">
                    <td className="py-2.5 pr-3 pl-5">
                      {REMARK_SOURCES[r.source as RemarkSource] ?? r.source}
                    </td>
                    <td className="num py-2.5 pr-3 text-right">{r.open}</td>
                    <td className={`num py-2.5 pr-3 text-right ${r.overdue ? 'text-danger' : ''}`}>
                      {r.overdue}
                    </td>
                    <td className="num py-2.5 pr-5 text-right">{r.avgFixDays ?? '—'}</td>
                  </tr>
                ))}
                {!s.bySource.length ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-4 text-ink-2">
                      Замечаний нет
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </ScrollX>
        </Panel>
        <Panel title="По подрядчикам" bodyClassName="p-0">
          <ScrollX>
            <table className="w-full min-w-[480px] text-[14px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                  <th className="py-2.5 pr-3 pl-5 font-medium">Подрядчик</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Открыто</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Просрочено</th>
                  <th className="py-2.5 pr-5 text-right font-medium">Устранение, дней</th>
                </tr>
              </thead>
              <tbody>
                {s.byContractor.map((r) => (
                  <tr key={r.name} className="border-t border-line">
                    <td className="py-2.5 pr-3 pl-5">{r.name}</td>
                    <td className="num py-2.5 pr-3 text-right">{r.open}</td>
                    <td className={`num py-2.5 pr-3 text-right ${r.overdue ? 'text-danger' : ''}`}>
                      {r.overdue}
                    </td>
                    <td className="num py-2.5 pr-5 text-right">{r.avgFixDays ?? '—'}</td>
                  </tr>
                ))}
                {!s.byContractor.length ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-4 text-ink-2">
                      Замечаний нет
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </ScrollX>
        </Panel>
      </div>
    </div>
  );
}

/* ───────────────────────── Создание ───────────────────────── */

/** Комплекты и листы проекта для привязки замечания или вопроса */
function useSheets(projectId: string) {
  const sets = useQuery({
    queryKey: ['psd-sets', projectId],
    queryFn: () => api<SetSummary[]>('/psd/sets', { query: { projectId } }),
    enabled: Boolean(projectId),
    retry: false,
  });
  return sets;
}

export function SheetPicker({
  projectId,
  setId,
  sheetId,
  onChange,
}: {
  projectId: string;
  setId: string;
  sheetId: string;
  onChange: (setId: string, sheetId: string) => void;
}) {
  const sets = useSheets(projectId);
  const detail = useQuery({
    queryKey: ['psd-set', setId],
    queryFn: () => api<{ sheets: Array<{ id: string; number: string; name: string }> }>(`/psd/sets/${setId}`),
    enabled: Boolean(setId),
  });
  if (sets.isError) return null;
  return (
    <>
      <Field label="Комплект ПСД">
        <Select className="h-11 md:h-10" value={setId} onChange={(e) => onChange(e.target.value, '')}>
          <option value="">— без привязки к листу —</option>
          {(sets.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.code}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Лист">
        <Select
          className="h-11 md:h-10"
          value={sheetId}
          disabled={!setId}
          onChange={(e) => onChange(setId, e.target.value)}
        >
          <option value="">—</option>
          {(detail.data?.sheets ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.number} {s.name}
            </option>
          ))}
        </Select>
      </Field>
    </>
  );
}

export function NewRemarkDialog({
  projectId,
  setId: initSet,
  sheetId: initSheet,
  point,
  visit,
  onClose,
}: {
  projectId?: string;
  setId?: string;
  sheetId?: string;
  /** Точка на листе (доли ширины и высоты), выбранная в просмотре */
  point?: { x: number; y: number };
  /** Замечание выдаётся на посещении надзора: источник — этот надзор, после выдачи — обратно в журнал */
  visit?: { id: string; kind: RemarkSource; number: string };
  onClose: () => void;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { list } = useProjectsWith('supervision.issue');
  const staff = me.tenant?.staff ?? false;
  const cps = useQuery({ ...counterpartiesQuery('contractor'), enabled: staff, retry: false });
  const [f, setF] = useState({
    projectId: projectId && list.some((p) => p.id === projectId) ? projectId : (list[0]?.id ?? ''),
    source: (visit?.kind ?? (staff ? 'builders' : 'tech_supervision')) as RemarkSource,
    kind: 'remark' as RemarkKind,
    title: '',
    description: '',
    normRef: '',
    setId: initSet ?? '',
    sheetId: initSheet ?? '',
    place: '',
    objectId: '',
    contractorId: '',
    assigneeId: '',
    dueDate: plusDays(7),
    stopWork: false,
  });
  const setProjectDefaultNewRemarkDialog = useCallback(
    (id: string) => setF((x) => ({ ...x, projectId: id })),
    [],
  );
  useProjectDefault(list, projectId, f.projectId, setProjectDefaultNewRemarkDialog);
  const project = useQuery({ ...projectQuery(f.projectId), enabled: Boolean(f.projectId) });
  const people = useQuery({
    queryKey: ['supervision-people', f.projectId, 'respond'],
    queryFn: () =>
      api<Array<{ id: string; fullName: string; external: boolean }>>('/supervision/people', {
        query: { projectId: f.projectId },
      }),
    enabled: Boolean(f.projectId),
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const internalSources = useSupervisionSettings().data?.internalSources ?? INTERNAL_SOURCES;
  const sources = (Object.keys(REMARK_SOURCES) as RemarkSource[]).filter(
    (s) => staff || !internalSources.includes(s),
  );
  // Источник стал внутренним по настройке компании — внешнему подставляется первый доступный
  useEffect(() => {
    if (sources.length && !sources.includes(f.source)) setF((x) => ({ ...x, source: sources[0]! }));
  }, [sources, f.source]);
  const m = useMutation({
    mutationFn: () =>
      api<RemarkDetail>('/supervision/remarks', {
        body: {
          projectId: f.projectId,
          source: f.source,
          kind: f.kind,
          title: f.title.trim(),
          description: f.description.trim() || null,
          normRef: f.normRef.trim() || null,
          sheetId: f.sheetId || null,
          ...(point && f.sheetId === initSheet ? { pointX: point.x, pointY: point.y } : {}),
          place: f.place.trim() || null,
          objectId: f.objectId || null,
          contractorId: f.contractorId || null,
          assigneeId: f.assigneeId || null,
          dueDate: f.dueDate,
          stopWork: f.stopWork,
          ...(visit ? { visitId: visit.id } : {}),
        },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['remarks'] });
      qc.invalidateQueries({ queryKey: ['psd-set'] });
      qc.invalidateQueries({ queryKey: ['visit'] });
      qc.invalidateQueries({ queryKey: ['visits'] });
      toast(`${REMARK_KINDS[r.kind]} ${r.number} выдано`);
      onClose();
      if (!visit) navigate({ to: '/supervision/remarks/$remarkId', params: { remarkId: r.id } });
    },
  });
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(v) => !v && onClose()}
      title={visit ? `Замечание на посещении ${visit.number}` : 'Новое замечание'}
      description="Фото «до» добавляются в карточке после сохранения."
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!f.projectId || f.title.trim().length < 3 || !f.dueDate}
            onClick={() => m.mutate()}
          >
            Выдать
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Проект" className="sm:col-span-2">
          <Select
            className="h-11 md:h-10"
            disabled={Boolean(visit)}
            value={f.projectId}
            onChange={(e) =>
              setF((x) => ({
                ...x,
                projectId: e.target.value,
                setId: '',
                sheetId: '',
                objectId: '',
                assigneeId: '',
              }))
            }
          >
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Источник">
          <Select
            className="h-11 md:h-10"
            disabled={Boolean(visit)}
            value={f.source}
            onChange={(e) => set('source', e.target.value as RemarkSource)}
          >
            {sources.map((k) => (
              <option key={k} value={k}>
                {REMARK_SOURCES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Вид">
          <Select
            className="h-11 md:h-10"
            value={f.kind}
            onChange={(e) => set('kind', e.target.value as RemarkKind)}
          >
            {(Object.keys(REMARK_KINDS) as RemarkKind[]).map((k) => (
              <option key={k} value={k}>
                {REMARK_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        {internalSources.includes(f.source) ? (
          <p className="text-[13px] text-ink-2 sm:col-span-2">
            Внутреннее замечание: подрядчик, заказчик и технадзор его не увидят.
          </p>
        ) : null}
        <Field label="Нарушение" className="sm:col-span-2">
          <Input
            className="h-11 md:h-10"
            value={f.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Нет песчаной подушки под трубой"
          />
        </Field>
        <Field label="Подробно" className="sm:col-span-2">
          <Textarea rows={3} value={f.description} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <Field label="Норма" hint="СП РК, СН РК, ГОСТ, пункт">
          <Input
            className="h-11 md:h-10"
            value={f.normRef}
            onChange={(e) => set('normRef', e.target.value)}
          />
        </Field>
        <Field label="Объект">
          <Select
            className="h-11 md:h-10"
            value={f.objectId}
            onChange={(e) => set('objectId', e.target.value)}
          >
            <option value="">—</option>
            {(project.data?.objects ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.code ? `${o.code} ` : ''}
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        <SheetPicker
          projectId={f.projectId}
          setId={f.setId}
          sheetId={f.sheetId}
          onChange={(s, sh) => setF((x) => ({ ...x, setId: s, sheetId: sh }))}
        />
        <Field label="Место" hint="Ось, пикет, отметка" className="sm:col-span-2">
          <Input className="h-11 md:h-10" value={f.place} onChange={(e) => set('place', e.target.value)} />
        </Field>
        {staff ? (
          <Field label="Подрядчик">
            <Select
              className="h-11 md:h-10"
              value={f.contractorId}
              onChange={(e) => set('contractorId', e.target.value)}
            >
              <option value="">—</option>
              {(cps.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.shortName || c.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Ответственный" hint="Получит поручение в «Ждёт меня»">
          <Select
            className="h-11 md:h-10"
            value={f.assigneeId}
            onChange={(e) => set('assigneeId', e.target.value)}
          >
            <option value="">—</option>
            {(people.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
                {p.external ? ' (внешний)' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Срок устранения">
          <Input
            type="date"
            min={todayLocal()}
            className="h-11 md:h-10"
            value={f.dueDate}
            onChange={(e) => set('dueDate', e.target.value)}
          />
        </Field>
        <FieldBasis field="remark.fix_days" className="sm:col-span-2" />
        <Checkbox
          className="sm:col-span-2"
          checked={f.stopWork}
          onChange={(e) => set('stopWork', e.target.checked)}
          label="Остановка работ до устранения"
        />
      </div>
      <ErrorBox error={m.error} className="mt-4" />
    </Dialog>
  );
}

export function NewQuestionDialog({
  projectId,
  setId: initSet,
  sheetId: initSheet,
  point,
  parent,
  onClose,
}: {
  projectId?: string;
  setId?: string;
  sheetId?: string;
  /** Точка на листе (доли ширины и высоты), выбранная в просмотре */
  point?: { x: number; y: number };
  parent?: { id: string; number: string; projectId: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { list } = useProjectsWith('supervision.issue', 'supervision.respond');
  const [f, setF] = useState({
    projectId:
      parent?.projectId ??
      (projectId && list.some((p) => p.id === projectId) ? projectId : (list[0]?.id ?? '')),
    kind: 'question' as 'question' | 'proposal',
    setId: initSet ?? '',
    sheetId: initSheet ?? '',
    subject: '',
    body: '',
    proposal: '',
    urgency: 'normal' as Urgency,
    blockedWorks: '',
    answerDays: '5',
    scheduleDays: '',
  });
  const setProjectDefaultNewQuestionDialog = useCallback(
    (id: string) => setF((x) => ({ ...x, projectId: id })),
    [],
  );
  useProjectDefault(list, parent?.projectId ?? projectId, f.projectId, setProjectDefaultNewQuestionDialog);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const m = useMutation({
    mutationFn: () =>
      api<QuestionDetail>('/supervision/questions', {
        body: {
          projectId: f.projectId,
          kind: f.kind,
          parentId: parent?.id ?? null,
          sheetId: f.sheetId || null,
          ...(point && f.sheetId === initSheet ? { pointX: point.x, pointY: point.y } : {}),
          subject: f.subject.trim(),
          body: f.body.trim(),
          proposal: f.proposal.trim() || null,
          urgency: f.urgency,
          blockedWorks: f.blockedWorks.trim() || null,
          answerDays: Number(f.answerDays) || 5,
          scheduleDays: f.kind === 'proposal' && f.scheduleDays ? Number(f.scheduleDays) : null,
        },
      }),
    onSuccess: (q) => {
      qc.invalidateQueries({ queryKey: ['questions'] });
      qc.invalidateQueries({ queryKey: ['psd-set'] });
      toast(`${q.number} зарегистрирован, на проверке ПТО`);
      onClose();
      navigate({ to: '/supervision/questions/$questionId', params: { questionId: q.id } });
    },
  });
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(v) => !v && onClose()}
      title={parent ? `Уточнение к ${parent.number}` : 'Новый вопрос к проекту'}
      description="ПТО проверит вопрос, объединит с похожими и передаст ГИП проектировщика. Фото добавляются в карточке."
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!f.projectId || f.subject.trim().length < 3 || f.body.trim().length < 3}
            onClick={() => m.mutate()}
          >
            Зарегистрировать
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {!parent ? (
          <>
            <Field label="Проект">
              <Select
                className="h-11 md:h-10"
                value={f.projectId}
                onChange={(e) => setF((x) => ({ ...x, projectId: e.target.value, setId: '', sheetId: '' }))}
              >
                {list.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} {p.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Вид">
              <Select
                className="h-11 md:h-10"
                value={f.kind}
                onChange={(e) => set('kind', e.target.value as 'question' | 'proposal')}
              >
                {(Object.keys(QUESTION_KINDS) as Array<'question' | 'proposal'>).map((k) => (
                  <option key={k} value={k}>
                    {QUESTION_KINDS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <SheetPicker
              projectId={f.projectId}
              setId={f.setId}
              sheetId={f.sheetId}
              onChange={(s, sh) => setF((x) => ({ ...x, setId: s, sheetId: sh }))}
            />
          </>
        ) : null}
        <Field label="Тема" className="sm:col-span-2">
          <Input
            className="h-11 md:h-10"
            value={f.subject}
            onChange={(e) => set('subject', e.target.value)}
          />
        </Field>
        <Field label="Суть вопроса" className="sm:col-span-2">
          <Textarea rows={4} value={f.body} onChange={(e) => set('body', e.target.value)} />
        </Field>
        <Field
          label={
            f.kind === 'proposal' ? 'Предлагаемое решение, экономия или удорожание' : 'Предлагаемое решение'
          }
          className="sm:col-span-2"
        >
          <Textarea rows={2} value={f.proposal} onChange={(e) => set('proposal', e.target.value)} />
        </Field>
        <Field label="Срочность">
          <Select
            className="h-11 md:h-10"
            value={f.urgency}
            onChange={(e) => set('urgency', e.target.value as Urgency)}
          >
            {(Object.keys(URGENCY) as Urgency[]).map((k) => (
              <option key={k} value={k}>
                {URGENCY[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Срок ответа, рабочих дней РК" hint="По договору с проектировщиком">
          <Input
            type="number"
            min={1}
            max={60}
            className="h-11 md:h-10"
            value={f.answerDays}
            onChange={(e) => set('answerDays', e.target.value)}
          />
        </Field>
        <Field label="Какие работы стоят" className="sm:col-span-2">
          <Input
            className="h-11 md:h-10"
            value={f.blockedWorks}
            onChange={(e) => set('blockedWorks', e.target.value)}
          />
        </Field>
        {f.kind === 'proposal' ? (
          <Field label="Влияние на срок, дней" hint="Минус — быстрее">
            <Input
              type="number"
              className="h-11 md:h-10"
              value={f.scheduleDays}
              onChange={(e) => set('scheduleDays', e.target.value)}
            />
          </Field>
        ) : null}
      </div>
      <ErrorBox error={m.error} className="mt-4" />
    </Dialog>
  );
}

/* ───────────────────────── Карточка замечания ───────────────────────── */

export function Events({
  events,
  statuses,
}: {
  events: RemarkDetail['events'];
  statuses: Record<string, string>;
}) {
  return (
    <ol className="flex flex-col gap-3 text-[14px]">
      {events.map((e, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: события идут по времени и не меняются
        <li key={i} className="border-l-2 border-line pl-3">
          <div>
            {e.action}
            {e.toStatus ? <span className="text-ink-2"> → {statuses[e.toStatus] ?? e.toStatus}</span> : null}
          </div>
          <div className="text-[12px] text-ink-2">
            {dateTime(e.at)}
            {e.by ? ` · ${e.by}` : ''}
          </div>
          {e.comment ? <div className="mt-1 whitespace-pre-wrap text-ink">{e.comment}</div> : null}
        </li>
      ))}
    </ol>
  );
}

function Photos({ photos }: { photos: RemarkDetail['photos'] }) {
  const label = { before: 'до', after: 'после', doc: 'документ' } as const;
  if (!photos.length) return <p className="text-[14px] text-ink-2">Фото нет.</p>;
  return (
    <ul className="flex flex-col gap-1 text-[14px]">
      {photos.map((p) => (
        <li key={p.id} className="flex flex-wrap items-center gap-2">
          <Badge tone={p.stage === 'after' ? 'success' : 'neutral'}>{label[p.stage]}</Badge>
          <FileLink id={p.id} name={p.name} kind="supervision" />
        </li>
      ))}
    </ul>
  );
}

function SheetRef({
  setId,
  setCode,
  sheetId,
  sheetNumber,
  revision,
  pointX,
  recheck,
}: {
  setId: string | null;
  setCode: string | null;
  sheetId: string | null;
  sheetNumber: string | null;
  revision: string | null;
  pointX: number | null;
  recheck: boolean;
}) {
  if (!sheetId) return <span className="text-ink-3">—</span>;
  return (
    <span className="flex flex-col gap-1">
      <Link
        to="/psd/sheets/$sheetId"
        params={{ sheetId }}
        className="inline-flex min-h-11 items-center gap-1 underline-offset-4 hover:underline md:min-h-0"
      >
        <MapPin className="size-4" />
        {setCode}, лист {sheetNumber}
        {revision ? `, ${revision}` : ''}
        {pointX !== null ? ' · точка на листе' : ''}
      </Link>
      {setId ? null : null}
      {recheck ? <Badge tone="warning">новая ревизия листа — проверить актуальность</Badge> : null}
    </span>
  );
}

export function RemarkPage() {
  const { remarkId } = useParams({ strict: false }) as { remarkId: string };
  const { visit: visitParam } = useSearch({ strict: false }) as { visit?: string };
  const qc = useQueryClient();
  // Открыто из журнала посещений: закрытие и возврат отмечаются на этом посещении (АВ-02)
  const visit = useQuery({
    queryKey: ['visit', visitParam],
    queryFn: () => api<VisitDetail>(`/supervision/visits/${visitParam}`),
    enabled: Boolean(visitParam),
    retry: false,
  });
  const q = useQuery({
    queryKey: ['remark', remarkId],
    queryFn: () => api<RemarkDetail>(`/supervision/remarks/${remarkId}`),
  });
  const me = useMe();
  const project = useQuery({ ...projectQuery(q.data?.projectId ?? ''), enabled: Boolean(q.data?.projectId) });
  const [change, setChange] = useState(false);
  const [act, setAct] = useState<RemarkAction | null>(null);
  useCrumbs(
    q.data
      ? [
          { label: q.data.projectCode, to: `/supervision?project=${q.data.projectId}` },
          ...(visit.data ? [{ label: visit.data.number, to: `/supervision/visits/${visit.data.id}` }] : []),
          { label: q.data.number },
        ]
      : null,
    q.error,
  );
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['remark', remarkId] });
    qc.invalidateQueries({ queryKey: ['remarks'] });
    qc.invalidateQueries({ queryKey: ['visit'] });
    qc.invalidateQueries({ queryKey: ['visits'] });
  };
  const onVisit =
    visit.data?.canIssue &&
    q.data &&
    visit.data.kind === q.data.source &&
    visit.data.projectId === q.data.projectId
      ? visit.data
      : null;
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const r = q.data;
  const main: RemarkAction[] = r.actions.filter((a) => a !== 'comment');
  const canPhoto = main.length > 0;
  const canChange = project.data
    ? can(me.access, 'changes.request', projectTarget(project.data)) && (me.tenant?.staff ?? false)
    : false;
  // Фото площадки, привязанные к замечанию в отчёте смены (ПР-03): площадка открыта сотрудникам с правом на неё
  const canSitePhotos = project.data
    ? (me.tenant?.staff ?? false) &&
      ['site.view', 'site.report', 'site.review'].some((x) => can(me.access, x, projectTarget(project.data!)))
    : false;
  return (
    <Page>
      <DiscussionOriginBanner toType="psd_remark" toId={r.id} />
      <PageHeader
        title={`${REMARK_KINDS[r.kind]} ${r.number}`}
        subtitle={<span className="[overflow-wrap:anywhere]">{r.title}</span>}
        actions={
          <>
            {main.map((a, i) => (
              <Button
                key={a}
                size="lg"
                variant={i === 0 ? 'primary' : a === 'cancel' ? 'danger' : 'secondary'}
                onClick={() => setAct(a)}
              >
                {REMARK_ACTIONS[a]}
              </Button>
            ))}
            {canChange && r.kind === 'instruction' ? (
              <Button size="lg" variant="secondary" onClick={() => setChange(true)}>
                Запрос на изменение
              </Button>
            ) : null}
            {r.actions.includes('comment') ? (
              <Button size="lg" variant="ghost" onClick={() => setAct('comment')}>
                Комментарий
              </Button>
            ) : null}
          </>
        }
      >
        {onVisit ? (
          <div className="mt-2 flex flex-col gap-1 rounded-md bg-info-soft px-3 py-2 text-[13px] text-ink md:flex-row md:items-center md:gap-3">
            <span>Проверка на посещении: закрытие или возврат попадут в его запись.</span>
            <Link
              to="/supervision/visits/$visitId"
              params={{ visitId: onVisit.id }}
              className="inline-flex min-h-11 items-center font-medium underline md:min-h-0"
            >
              Вернуться к посещению {onVisit.number}
            </Link>
          </div>
        ) : null}
        <div className="mt-2 flex flex-wrap gap-1.5">
          <RemarkBadge status={r.status} overdue={r.overdue} />
          {r.stopWork ? (
            <Badge tone="danger">
              <AlertTriangle className="size-3.5" /> остановка работ
            </Badge>
          ) : null}
          {r.internal ? <Badge>внутреннее</Badge> : null}
        </div>
      </PageHeader>
      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="flex min-w-0 flex-col gap-4">
          <Panel title="Суть">
            <KeyValue
              items={[
                { label: 'Источник', value: REMARK_SOURCES[r.source] },
                {
                  label: 'Описание',
                  value: r.description ? <span className="whitespace-pre-wrap">{r.description}</span> : null,
                },
                { label: 'Норма', value: r.normRef },
                { label: 'Лист ПСД', value: <SheetRef {...r} /> },
                { label: 'Объект', value: r.objectName },
                { label: 'Место', value: r.place },
                { label: 'Подрядчик', value: r.contractorName },
                { label: 'Ответственный', value: r.assigneeName },
                {
                  label: 'Срок',
                  value: <span className={r.overdue ? 'text-danger' : ''}>{date(r.dueDate)}</span>,
                },
                { label: 'Выдал', value: `${r.issuedBy ?? '—'}, ${dateTime(r.issuedAt)}` },
                {
                  label: 'Посещение',
                  value:
                    r.visitId && r.visitNumber ? (
                      <Link
                        to="/supervision/visits/$visitId"
                        params={{ visitId: r.visitId }}
                        className="inline-flex min-h-11 items-center underline md:min-h-0"
                      >
                        {r.visitNumber}
                      </Link>
                    ) : null,
                },
                {
                  label: 'Создано по чек-листу',
                  value: r.checklist ? (
                    <Link
                      to="/qhse/inspections/$inspectionId"
                      params={{ inspectionId: r.checklist.id }}
                      className="inline-flex min-h-11 items-center underline md:min-h-0"
                    >
                      {r.checklist.number}, п. {r.checklist.position}
                    </Link>
                  ) : null,
                },
                {
                  label: 'Коренная причина',
                  value: r.rootCause ? ROOT_CAUSES[r.rootCause as RootCause] : null,
                },
                {
                  label: 'Фото площадки',
                  value: canSitePhotos ? (
                    <Link
                      {...sitePhotosLink(r.projectId, { remark: r.id })}
                      className="inline-flex min-h-11 items-center underline md:min-h-0"
                    >
                      Фото из отчётов смены
                    </Link>
                  ) : null,
                },
              ]}
            />
          </Panel>
          <Panel
            title="Фото и документы"
            actions={
              canPhoto ? (
                <>
                  <UploadButton
                    url={`/supervision/remarks/${r.id}/files`}
                    fields={{ stage: 'before' }}
                    accept={PHOTO_ACCEPT}
                    label="Фото «до»"
                    onDone={refresh}
                  />
                  <UploadButton
                    url={`/supervision/remarks/${r.id}/files`}
                    fields={{ stage: 'after' }}
                    accept={PHOTO_ACCEPT}
                    label="Фото «после»"
                    onDone={refresh}
                  />
                </>
              ) : null
            }
          >
            <Photos photos={r.photos} />
          </Panel>
          <DiscussionPanel entityType="psd_remark" entityId={r.id} projectId={r.projectId} />
        </div>
        <Panel title="Ход">
          <Events events={r.events} statuses={REMARK_STATUSES} />
        </Panel>
      </div>
      {act ? (
        <RemarkActionDialog
          remark={r}
          action={act}
          visitId={onVisit && ['close', 'return', 'comment'].includes(act) ? onVisit.id : undefined}
          onClose={() => setAct(null)}
          onDone={refresh}
        />
      ) : null}
      {change ? (
        <NewChangeDialog
          projectId={r.projectId}
          preset={{
            title: `По указанию ${r.number}: ${r.title}`.slice(0, 300),
            reason: r.description ?? r.title,
            basis: `${REMARK_KINDS[r.kind]} ${r.number}`,
            remarkId: r.id,
            type: 'psd',
          }}
          onClose={() => setChange(false)}
        />
      ) : null}
    </Page>
  );
}

function RemarkActionDialog({
  remark,
  action,
  visitId,
  onClose,
  onDone,
}: {
  remark: RemarkDetail;
  action: RemarkAction;
  visitId?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [comment, setComment] = useState('');
  const [cause, setCause] = useState<RootCause | ''>('');
  const [due, setDue] = useState('');
  const needComment = ['dispute', 'return', 'cancel', 'reopen', 'comment'].includes(action);
  const needCause = action === 'close' && remark.kind !== 'instruction';
  const needAfterPhoto = (action === 'fix' || action === 'close') && remark.kind !== 'instruction';
  const hasAfterPhoto = remark.photos.some((p) => p.stage === 'after');
  const m = useMutation({
    mutationFn: () =>
      api<RemarkDetail>(`/supervision/remarks/${remark.id}/action`, {
        body: {
          action,
          comment: comment.trim() || null,
          rootCause: cause || null,
          ...(due ? { dueDate: due } : {}),
          ...(visitId ? { visitId } : {}),
        },
      }),
    onSuccess: () => {
      onDone();
      toast(`${remark.number}: ${REMARK_ACTIONS[action].toLowerCase()} — готово`);
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`${REMARK_ACTIONS[action]}: ${remark.number}`}
      description={
        action === 'fix'
          ? 'Приложите фото «после» в карточке — по ним выдавший проверит устранение.'
          : action === 'close'
            ? 'Закрытие — проверка выдавшего: нужно фото «после» устранения и коренная причина.'
            : action === 'dispute'
              ? 'Обоснуйте, почему замечание неверно.'
              : undefined
      }
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant={action === 'cancel' ? 'danger' : 'primary'}
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={
              (needComment && !comment.trim()) || (needCause && !cause) || (needAfterPhoto && !hasAfterPhoto)
            }
            onClick={() => m.mutate()}
          >
            {REMARK_ACTIONS[action]}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {needAfterPhoto && !hasAfterPhoto ? (
          <p className="text-sm text-danger" role="alert">
            Сначала загрузите фото «после» в карточке замечания, затем повторите действие.
          </p>
        ) : null}
        {needCause ? (
          <Field label="Коренная причина">
            <Select
              className="h-11 md:h-10"
              value={cause}
              onChange={(e) => setCause(e.target.value as RootCause)}
            >
              <option value="">— выберите —</option>
              {(Object.keys(ROOT_CAUSES) as RootCause[]).map((k) => (
                <option key={k} value={k}>
                  {ROOT_CAUSES[k]}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {action === 'return' || action === 'reopen' ? (
          <Field label="Новый срок устранения">
            <Input
              type="date"
              min={todayLocal()}
              className="h-11 md:h-10"
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </Field>
        ) : null}
        <Field label={needComment ? 'Комментарий (обязательно)' : 'Комментарий'}>
          <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/* ───────────────────────── Карточка вопроса ───────────────────────── */

export function QuestionPage() {
  const { questionId } = useParams({ strict: false }) as { questionId: string };
  const qc = useQueryClient();
  const me = useMe();
  const q = useQuery({
    queryKey: ['question', questionId],
    queryFn: () => api<QuestionDetail>(`/supervision/questions/${questionId}`),
  });
  const project = useQuery({ ...projectQuery(q.data?.projectId ?? ''), enabled: Boolean(q.data?.projectId) });
  const [act, setAct] = useState<QuestionAction | null>(null);
  const [clarify, setClarify] = useState(false);
  useCrumbs(
    q.data
      ? [
          { label: 'Вопросы к проекту', to: `/supervision?tab=questions&project=${q.data.projectId}` },
          { label: q.data.number },
        ]
      : null,
    q.error,
  );
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['question', questionId] });
    qc.invalidateQueries({ queryKey: ['questions'] });
  };
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const x = q.data;
  const main = x.actions.filter((a) => a !== 'comment');
  const canAsk = project.data
    ? can(me.access, 'supervision.respond', projectTarget(project.data)) ||
      can(me.access, 'supervision.issue', projectTarget(project.data))
    : false;
  return (
    <Page>
      <PageHeader
        title={`${x.kind === 'proposal' ? 'Предложение' : 'Вопрос'} ${x.number}`}
        subtitle={<span className="[overflow-wrap:anywhere]">{x.subject}</span>}
        actions={
          <>
            {main.map((a, i) => (
              <Button
                key={a}
                size="lg"
                variant={i === 0 ? 'primary' : a === 'cancel' ? 'danger' : 'secondary'}
                onClick={() => setAct(a)}
              >
                {QUESTION_ACTIONS[a]}
              </Button>
            ))}
            {canAsk && !x.parentId ? (
              <Button size="lg" variant="ghost" onClick={() => setClarify(true)}>
                Уточняющий вопрос
              </Button>
            ) : null}
            {x.actions.includes('comment') ? (
              <Button size="lg" variant="ghost" onClick={() => setAct('comment')}>
                Комментарий
              </Button>
            ) : null}
          </>
        }
      >
        <div className="mt-2 flex flex-wrap gap-1.5">
          <QuestionBadge status={x.status} overdue={x.overdue} />
          {x.side ? <Badge tone="neutral">сейчас: {x.side}</Badge> : null}
          {x.urgency !== 'normal' ? (
            <Badge tone={x.urgency === 'critical' ? 'danger' : 'warning'}>{URGENCY[x.urgency]}</Badge>
          ) : null}
          {x.changesDesign ? (
            <Badge tone="accent">ответ меняет проект — нужен запрос на изменение</Badge>
          ) : null}
        </div>
      </PageHeader>
      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="flex min-w-0 flex-col gap-4">
          <DiscussionOriginBanner toType="psd_question" toId={x.id} />
          <Panel title="Вопрос">
            <p className="whitespace-pre-wrap text-[14px] text-ink">{x.body}</p>
            {x.proposal ? (
              <div className="mt-3 rounded-md bg-surface-2 p-3 text-[14px]">
                <div className="text-[12px] text-ink-2">Предлагаемое решение</div>
                <div className="whitespace-pre-wrap">{x.proposal}</div>
              </div>
            ) : null}
          </Panel>
          {x.answer ? (
            <Panel
              title="Ответ проектировщика"
              meta={
                x.answeredAt
                  ? `${dateTime(x.answeredAt)}${x.answeredBy ? `, ${x.answeredBy}` : ''}`
                  : undefined
              }
            >
              <p className="whitespace-pre-wrap text-[14px] text-ink">{x.answer}</p>
            </Panel>
          ) : null}
          <Panel title="Где и что">
            <KeyValue
              items={[
                { label: 'Лист ПСД', value: <SheetRef {...x} /> },
                { label: 'Объект', value: x.objectName },
                { label: 'Какие работы стоят', value: x.blockedWorks },
                {
                  label: 'Срок ответа',
                  value: x.dueDate ? (
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      <span className={x.overdue ? 'text-danger' : ''}>{date(x.dueDate)}</span>
                      <ReminderBadge stage={x.reminderStage} />
                    </span>
                  ) : (
                    'с момента передачи проектировщику'
                  ),
                },
                { label: 'Влияние на срок', value: x.scheduleDays !== null ? `${x.scheduleDays} дн.` : null },
                { label: 'Автор', value: `${x.authorName ?? '—'}, ${dateTime(x.createdAt)}` },
                {
                  label: 'Исходный вопрос',
                  value: x.parentId ? (
                    <Link
                      to="/supervision/questions/$questionId"
                      params={{ questionId: x.parentId }}
                      className="underline-offset-4 hover:underline"
                    >
                      {x.parentNumber}
                    </Link>
                  ) : null,
                },
                {
                  label: 'Объединён с',
                  value: x.mergedInto ? (
                    <Link
                      to="/supervision/questions/$questionId"
                      params={{ questionId: x.mergedInto.id }}
                      className="underline-offset-4 hover:underline"
                    >
                      {x.mergedInto.number}
                    </Link>
                  ) : null,
                },
                {
                  label: 'Уточнения',
                  value: x.children.length ? (
                    <span className="flex flex-wrap gap-2">
                      {x.children.map((c) => (
                        <Link
                          key={c.id}
                          to="/supervision/questions/$questionId"
                          params={{ questionId: c.id }}
                          className="underline-offset-4 hover:underline"
                        >
                          {c.number} (
                          {QUESTION_STATUSES[c.status as keyof typeof QUESTION_STATUSES] ?? c.status})
                        </Link>
                      ))}
                    </span>
                  ) : null,
                },
              ]}
            />
          </Panel>
          {x.works ? (
            <QuestionWorks
              questionId={x.id}
              projectId={x.projectId}
              works={x.works}
              open={x.canLinkWorks && !['accepted', 'merged', 'cancelled'].includes(x.status)}
              onChange={refresh}
            />
          ) : null}
          <Panel
            title="Фото"
            actions={
              main.length ? (
                <UploadButton
                  url={`/supervision/questions/${x.id}/files`}
                  fields={{ stage: 'doc' }}
                  accept={PHOTO_ACCEPT}
                  label="Добавить фото"
                  onDone={refresh}
                />
              ) : null
            }
          >
            <Photos photos={x.photos} />
          </Panel>
        </div>
        <Panel title="Ход">
          <Events events={x.events} statuses={QUESTION_STATUSES} />
        </Panel>
      </div>
      {act ? (
        <QuestionActionDialog question={x} action={act} onClose={() => setAct(null)} onDone={refresh} />
      ) : null}
      {clarify ? (
        <NewQuestionDialog
          parent={{ id: x.id, number: x.number, projectId: x.projectId }}
          onClose={() => setClarify(false)}
        />
      ) : null}
    </Page>
  );
}

function QuestionActionDialog({
  question,
  action,
  onClose,
  onDone,
}: {
  question: QuestionDetail;
  action: QuestionAction;
  onClose: () => void;
  onDone: () => void;
}) {
  const [comment, setComment] = useState('');
  const [changes, setChanges] = useState(question.changesDesign);
  const [mergeId, setMergeId] = useState('');
  const [notify, setNotify] = useState<string[]>([]);
  const myId = useMe().user.id;
  // Кому разослать принятый ответ (ВП-06): участники проекта, которые видят надзор
  const people = useQuery({
    queryKey: ['supervision-people', question.projectId, 'view'],
    queryFn: () =>
      api<Array<{ id: string; fullName: string; external: boolean }>>('/supervision/people', {
        query: { projectId: question.projectId, perm: 'view' },
      }),
    enabled: action === 'accept',
  });
  const others = useQuery({
    queryKey: ['questions', { project: question.projectId, status: 'open' }],
    queryFn: () =>
      api<QuestionSummary[]>('/supervision/questions', {
        query: { projectId: question.projectId, status: 'open' },
      }),
    enabled: action === 'merge',
  });
  const needComment = ['answer', 'clarify', 'cancel', 'comment'].includes(action);
  const m = useMutation({
    mutationFn: () =>
      api<QuestionDetail>(`/supervision/questions/${question.id}/action`, {
        body: {
          action,
          comment: comment.trim() || null,
          ...(action === 'answer' || action === 'accept' ? { changesDesign: changes } : {}),
          ...(action === 'merge' ? { mergeIntoId: mergeId } : {}),
          ...(action === 'accept' ? { notifyIds: notify } : {}),
        },
      }),
    onSuccess: () => {
      onDone();
      toast(`${question.number}: ${QUESTION_ACTIONS[action].toLowerCase()} — готово`);
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`${QUESTION_ACTIONS[action]}: ${question.number}`}
      description={
        action === 'to_designer'
          ? 'Срок ответа отсчитывается в рабочих днях РК с сегодняшнего дня.'
          : action === 'clarify'
            ? 'Вопрос вернётся проектировщику с вашим уточнением и новым сроком.'
            : undefined
      }
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant={action === 'cancel' ? 'danger' : 'primary'}
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={(needComment && !comment.trim()) || (action === 'merge' && !mergeId)}
            onClick={() => m.mutate()}
          >
            {QUESTION_ACTIONS[action]}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {action === 'merge' ? (
          <Field label="С каким вопросом объединить">
            <Select className="h-11 md:h-10" value={mergeId} onChange={(e) => setMergeId(e.target.value)}>
              <option value="">— выберите —</option>
              {(others.data ?? [])
                .filter((o) => o.id !== question.id)
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.number} {o.subject}
                  </option>
                ))}
            </Select>
          </Field>
        ) : null}
        <Field
          label={action === 'answer' ? 'Ответ' : needComment ? 'Комментарий (обязательно)' : 'Комментарий'}
        >
          <Textarea
            rows={action === 'answer' ? 6 : 3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Field>
        {action === 'accept' && people.data?.some((p) => !p.external && p.id !== myId) ? (
          <Field
            label="Разослать ответ"
            hint="Сотрудники проекта получат поручение «ознакомиться»; ответ сохранится PDF-документом у вопроса"
          >
            <div className="grid max-h-56 gap-1 overflow-auto rounded-md border border-line p-2">
              {(people.data ?? [])
                .filter((p) => !p.external && p.id !== myId)
                .map((p) => (
                  <Checkbox
                    key={p.id}
                    className="min-h-11 items-center md:min-h-0"
                    checked={notify.includes(p.id)}
                    onChange={(e) =>
                      setNotify((x) => (e.target.checked ? [...x, p.id] : x.filter((y) => y !== p.id)))
                    }
                    label={p.fullName}
                  />
                ))}
            </div>
          </Field>
        ) : null}
        {action === 'answer' || action === 'accept' ? (
          <Checkbox
            checked={changes}
            onChange={(e) => setChanges(e.target.checked)}
            label="Ответ меняет проект"
            description="Нужна новая ревизия ПСД и запрос на изменение"
          />
        ) : null}
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}
